import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import {
  POSTTYPES,
  createArrayOfObjects,
  extractDate,
  getUTCDate,
} from "../../../utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  ContainerItem,
  Input,
  InputDate,
  Label,
  Select,
  Text,
} from "../../../components";
import BrandSelection from "../../common/brandSelection";
import { palette } from "../../../theme";
import { Box } from "@mui/material";
import PostCard from "./common/postCard";
import PostCardV2 from "./common/postCardV2";
import { api, apiEndpoints } from "../../../services/api";
import { showSnackbar } from "../../../components/snackbar/snackbarSlice";
import { SNACKBAR_TYPES } from "../../../components/snackbar/snackbarTypes";
import { clearSelectedPost, selectPostSelected } from "../postSlice";
import { ReactComponent as TrashIcon } from "../../../assets/trash_icon.svg";

const CreatePostV2 = () => {
  const [initialValues, setInitialValues] = useState({
    brand: "",
    selectAllLocations: true,
    locations: "",
    type: POSTTYPES.POST,
    caption: "",
    banner: "",
    options: [],
    startDate: "",
    endDate: "",
    noOfOptions: null,
  });
  const [selectedBannerImage, setSelectedBannerImage] = useState();
  const [previewBannerImage, setPreviewBannerImage] = useState();
  const [clearLocations, setClearLocations] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hiddenFileInput = React.useRef(null);
  const formRef = React.useRef(null);
  const now_utc = getUTCDate();
  const selectedPost = useSelector(selectPostSelected);
  const isEditing = Boolean(selectedPost);

  const noOfOptions = [
    { label: "2", value: 2 },
    { label: "3", value: 3 },
    { label: "4", value: 4 },
    { label: "5", value: 5 },
    { label: "6", value: 6 },
  ];
  const postTypeOptions = [
    { label: "Content Post", value: POSTTYPES.POST },
    { label: "Poll", value: POSTTYPES.POLL },
  ];

  const handleBannerImageUpload = (event) => {
    const selectedFile = event.target.files[0];

    if (selectedFile && selectedFile.type.includes("image")) {
      setSelectedBannerImage(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewBannerImage(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setSelectedBannerImage(null);
      setPreviewBannerImage(null);
      console.error("Invalid file selected. Please choose an image file.");
    }
  };

  const validate = (values) => {
    const errors = {};
    if (!values.type) {
      errors.type = "This field is required";
    }
    if (values.type === POSTTYPES.POST) {
      if (!values.banner) {
        errors.banner = "This field is required";
      }
    }
    if (!values.brand) {
      errors.brand = "This field is required";
    }

    if (!values.selectAllLocations && !values.locations) {
      errors.locations = "This field is required";
    }
    if (values.type === POSTTYPES.POLL) {
      if (values.caption === "") {
        errors.caption = "This field is required";
      }
      if (values.noOfOptions === "") {
        errors.noOfOptions = "This field is required";
      }
      if (!values.options.length) {
        errors.options = "This field is required";
      }
      values.options.forEach((value, index) => {
        if (!value) {
          errors[`options[${index}]`] = "Field is required";
        }
      });
    }
    return errors;
  };

  const onSubmit = async (values, resetForm) => {
    const formData = new FormData();
    formData.append(
      "target_ids",
      values.selectAllLocations ? values.brand : values.locations
    );
    if (values.type === POSTTYPES.POST) {
      formData.append(
        "type",
        values.banner && values.banner !== "" ? "image with text" : "text"
      );
      formData.append("verb", values.caption);
      formData.append("file", selectedBannerImage);
    } else {
      formData.append("type", POSTTYPES.POLL);
      formData.append("verb", values.caption);
      formData.append("options", JSON.stringify(values.options));
      if (values.startDate !== "") {
        formData.append("start_date", extractDate(values.startDate));
      }
      if (values.endDate !== "") {
        formData.append("end_date", extractDate(values.endDate));
      }
    }
    try {
      await api.post(apiEndpoints.managePosts(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      dispatch(
        showSnackbar({
          message: `${
            values.type === POSTTYPES.POLL ? "Poll" : "Post"
          } created successfully`,
          type: SNACKBAR_TYPES.SNACKBAR_SUCCESS,
        })
      );
      resetForm();
      setClearLocations(true);
      setSelectedBannerImage(null);
      setPreviewBannerImage(null);
      navigate("/posts?tab=manage");
    } catch (e) {
      dispatch(
        showSnackbar({
          message: `Unable to create ${
            values.type === POSTTYPES.POLL ? "poll" : "post"
          }`,
          type: SNACKBAR_TYPES.SNACKBAR_ERROR,
        })
      );
    }
  };

  const onEdit = async (values, resetForm) => {
    const formData = new FormData();
    formData.append("post_id", values.id);
    formData.append("group_id", values.brand);

    if (values.caption !== selectedPost.caption) {
      formData.append("verb", values.caption);
    }

    if (values.type === POSTTYPES.POST) {
      if (selectedBannerImage) {
        formData.append("file", selectedBannerImage);
      }
    } else {
      formData.append("options", JSON.stringify(values.options));
      formData.append("end_date", values.endDate);
      formData.append("start_date", values.startDate);
    }

    try {
      await api.put(apiEndpoints.managePosts(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      dispatch(
        showSnackbar({
          message: `${
            values.type === POSTTYPES.POLL ? "Poll" : "Post"
          } updated successfully`,
          type: SNACKBAR_TYPES.SNACKBAR_SUCCESS,
        })
      );
      resetForm();
      setSelectedBannerImage(null);
      setPreviewBannerImage(null);
      navigate("/posts?tab=manage");
    } catch (e) {
      dispatch(
        showSnackbar({
          message: `Unable to update ${
            values.type === POSTTYPES.POLL ? "poll" : "post"
          }`,
          type: SNACKBAR_TYPES.SNACKBAR_ERROR,
        })
      );
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    if (isEditing) {
      await onEdit(values, resetForm);
    } else {
      await onSubmit(values, resetForm);
    }
  };

  useEffect(() => {
    if (selectedPost) {
      setInitialValues({
        ...selectedPost,
        selectAllLocations: true,
        noOfOptions: selectedPost.options.length,
      });
    }

    return () => {
      dispatch(clearSelectedPost());
    };
  }, [selectedPost]);

  return (
    <Formik
      innerRef={formRef}
      values={initialValues}
      enableReinitialize
      initialValues={initialValues}
      validateOnBlur
      validateOnChange
      validate={validate}
      initialTouched={false}
      isInitialValid={false}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        isValid,
        setFieldValue,
        handleChange,
        handleBlur,
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit}>
          <Container>
            {!isEditing && (
              <Container padding="0" width="100%">
                <BrandSelection
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  readOnly={false}
                  clearLocations={clearLocations}
                  setClearLocations={setClearLocations}
                />
              </Container>
            )}
            <Container width="345px" margin="0 200px 0 0">
              <ContainerItem padding="8px 0" width="100%">
                <Select
                  readOnly={isEditing}
                  name="type"
                  label="Select post type"
                  value={values.type}
                  options={postTypeOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.type}
                />
              </ContainerItem>
              {values.type === POSTTYPES.POLL ? (
                <>
                  <ContainerItem padding="8px 0" width="100%">
                    <Input
                      name="caption"
                      label="Enter question"
                      value={values.caption}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.caption}
                      type="text"
                    />
                  </ContainerItem>
                  <ContainerItem padding="8px 0" width="100%">
                    <Select
                      name="noOfOptions"
                      label="Select number of options"
                      value={values.noOfOptions}
                      options={noOfOptions}
                      onChange={(e) => {
                        handleChange(e);
                        const selectedValue = parseInt(e.target.value);
                        const currentLength = values.options.length;

                        if (selectedValue === currentLength) {
                          return; // No need to make any changes
                        } else if (selectedValue < currentLength) {
                          const newOptions = values.options.slice(
                            0,
                            selectedValue
                          );

                          setFieldValue("options", newOptions);
                        } else {
                          const newOptions = [];

                          for (
                            let i = 1;
                            i <= selectedValue - currentLength;
                            i++
                          ) {
                            newOptions.push({
                              label: "",
                              value: `option_${currentLength + i}`,
                            });
                          }
                          setFieldValue("options", [
                            ...values.options,
                            ...newOptions,
                          ]);
                        }
                      }}
                      onBlur={handleBlur}
                      error={
                        touched.noOfOptions && errors.noOfOptions
                          ? errors.noOfOptions
                          : null
                      }
                    />
                  </ContainerItem>
                  <ContainerItem padding="8px 0" width="100%">
                    {values.options.map((option, index) => {
                      return (
                        <ContainerItem flex>
                          <Input
                            label={`Option ${index + 1}`}
                            name={`options[${index}]`}
                            value={option.label}
                            onChange={(e) => {
                              setFieldValue(`options[${index}]`, {
                                ...option,
                                label: e.target.value,
                              });
                            }}
                            onBlur={handleBlur}
                            error={errors[`options[${index}]`]}
                          />
                          <TrashIcon
                            style={{
                              marginLeft: "16px",
                              marginTop: "4px",
                              cursor:
                                values.options.length > 2
                                  ? "pointer"
                                  : "not-allowed",
                            }}
                            onClick={() => {
                              if (values.options.length > 2) {
                                const opts = [...values.options];
                                opts.splice(index, 1);
                                setFieldValue(`options`, opts);
                                setFieldValue("noOfOptions", opts.length);
                              }
                            }}
                          />
                        </ContainerItem>
                      );
                    })}
                  </ContainerItem>
                </>
              ) : (
                <>
                  <ContainerItem padding="8px 0" width="100%">
                    <Input
                      name="caption"
                      value={values.caption}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      hint="NOTE: Character count limited to 120"
                      hintStyles={{ color: palette.text.purple }}
                      label="Enter description"
                      multiline
                      minRows={5}
                      characterLimit={120}
                      error={errors.caption}
                    />
                  </ContainerItem>
                  <ContainerItem width="100%" padding="0" margin="8px 0 0 0">
                    <Container
                      padding="0"
                      align="center"
                      justify="space-between"
                    >
                      <ContainerItem padding="0">
                        <Button
                          label="Upload Photo"
                          variant="contained"
                          onClick={() => {
                            hiddenFileInput.current.click();
                          }}
                        />
                        <input
                          name="banner"
                          type="file"
                          accept="image/*"
                          ref={hiddenFileInput}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            handleBannerImageUpload(e);
                            setFieldValue("banner", e.target.files[0].name);
                          }}
                        />
                      </ContainerItem>
                    </Container>
                  </ContainerItem>
                </>
              )}
              {values.type ? (
                <ContainerItem flex width="100%" padding="40px 0 8px 0">
                  {isEditing && (
                    <Button
                      label="Cancel"
                      variant="outlined"
                      onClick={() => {
                        navigate("/posts?tab=manage");
                        dispatch(clearSelectedPost());
                      }}
                      styles={{ marginRight: "16px", width: "120px" }}
                    />
                  )}
                  <Button
                    isLoading={isSubmitting}
                    disabled={!isValid}
                    label={`${isEditing ? "Update" : "Publish"} ${
                      values.type === POSTTYPES.POLL ? "Poll" : "Post"
                    }`}
                    variant="contained"
                    type="submit"
                  />
                </ContainerItem>
              ) : null}
            </Container>
            <Container width="345px">
              {values.type === POSTTYPES.POLL && (
                <>
                  <ContainerItem padding="8px 0" width="100%">
                    <InputDate
                      label="Start Date"
                      labelStyles={{
                        fontWeight: "400",
                        fontSize: "16px",
                      }}
                      name="startDate"
                      value={values.startDate}
                      minDate={now_utc}
                      maxDate={values.endDate}
                      handleDate={(date) => {
                        setFieldValue("startDate", date);
                      }}
                      error={
                        touched.startDate && errors.startDate
                          ? errors.startDate
                          : null
                      }
                    />
                  </ContainerItem>
                  <ContainerItem
                    padding="8px 0"
                    margin="0 0 40px 0"
                    width="100%"
                  >
                    <InputDate
                      label="End Date"
                      labelStyles={{
                        fontWeight: "400",
                        fontSize: "16px",
                      }}
                      name="endDate"
                      value={values.endDate}
                      minDate={
                        values.startDate !== "" ? values.startDate : now_utc
                      }
                      handleDate={(date) => {
                        setFieldValue("endDate", date);
                      }}
                      error={
                        touched.endDate && errors.endDate
                          ? errors.endDate
                          : null
                      }
                    />
                  </ContainerItem>
                </>
              )}
              <Box
                sx={{
                  width: "404px",
                  height: "auto",
                  backgroundColor: palette.background.darkPlum,
                  borderRadius: "10px",
                  // marginTop: "56px",
                  padding: "28px 56px 56px 56px",
                  boxSizing: "border-box",
                }}
              >
                <ContainerItem padding="0 0 18px 0" width="100%">
                  <Text
                    content="Live Preview"
                    sx={{
                      fontSize: "24px",
                      fontWeight: 700,
                      color: palette.text.white,
                      margin: "0 68px",
                    }}
                  />
                </ContainerItem>
                <PostCardV2
                  isEditing={isEditing}
                  values={values}
                  previewBannerImage={previewBannerImage}
                  fileRef={hiddenFileInput}
                />
              </Box>
            </Container>
          </Container>
        </form>
      )}
    </Formik>
  );
};

export default CreatePostV2;
